import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Eraser, Pencil, Plus, Search, X } from "lucide-react";
import { applyFindingPaletteColor, PULP_OUTLINE_COLOR, readableTextColor } from "./findingPalette.config";
import { getSinglePenStyle, isSinglePenCode } from "./singlePenStyles";
import { normalizeChartFinding } from "./chartFindingBehavior";

const SINGLE_CONDITION_PEN_CATEGORIES = new Set(["pulpitis", "mobility", "periapical_lesion"]);

const SECTION_LABELS = {
  core_top10: "Core",
  core_more: "More findings",
  expanded_more: "Expanded findings",
  single_pen: "Single pens",
  custom: "Custom",
  legacy: "Catalog",
};

const NEUTRAL_ACTIVE = {
  fill: "#f8fafc",
  border: "#cbd5e1",
  text: "#334155",
  activeFill: "#e2e8f0",
  activeBorder: "#475569",
  activeText: "#0f172a",
};

export function flattenPaletteFindings(categories = []) {
  const out = [];
  for (const rawCategory of categories) {
    const category = applyFindingPaletteColor(rawCategory);
    const categoryChartLabel = category.chartLabel || category.chart_label || category.label;
    const categoryReadbackLabel = category.readbackLabel || category.readback_label || category.patientLabel || category.patient_label || categoryChartLabel;
    out.push(normalizeChartFinding({
      id: category.id,
      code: category.code,
      display_name: categoryChartLabel,
      name: categoryChartLabel,
      patient_label: category.patientLabel,
      chart_label: categoryChartLabel,
      readback_label: categoryReadbackLabel,
      clinical_tooltip: category.clinicalTooltip || category.clinical_tooltip,
      color_hex: category.colorHex,
      text_color_hex: category.textColorHex || readableTextColor(category.colorHex),
      requires_surfaces: !!category.requiresSurfaces,
      display_order: category.displayOrder ?? 100,
      palette_section: category.paletteSection,
      has_severity: !!category.hasSeverity,
      default_severity_code: category.defaultSeverityCode,
      category_code: category.code,
      letter_abbreviation: category.letterAbbreviation,
      shape_marker: category.shapeMarker,
      single_pen_style: category.singlePenStyle,
      is_custom: !!category.isCustom,
      is_palette_category: true,
    }));

    for (const level of category.severityLevels || []) {
      const levelChartLabel = level.chartLabel || level.chart_label || level.label;
      const levelReadbackLabel = level.readbackLabel || level.readback_label || level.patientLabel || level.patient_label || levelChartLabel;
      out.push(normalizeChartFinding({
        id: level.id,
        code: level.code,
        display_name: `${categoryChartLabel} - ${levelChartLabel}`,
        name: `${categoryChartLabel} - ${levelChartLabel}`,
        patient_label: `${category.patientLabel || categoryChartLabel} - ${level.patientLabel || levelChartLabel}`,
        chart_label: levelChartLabel,
        readback_label: levelReadbackLabel,
        color_hex: level.colorHex,
        text_color_hex: level.textColorHex || readableTextColor(level.colorHex),
        requires_surfaces: !!category.requiresSurfaces,
        display_order: level.displayOrder ?? category.displayOrder ?? 100,
        palette_section: category.paletteSection,
        category_code: category.code,
        category_label: categoryChartLabel,
        category_readback_label: categoryReadbackLabel,
        clinical_tooltip: category.clinicalTooltip || category.clinical_tooltip,
        severity_rank: level.rank,
        shade_stop: level.shadeStop,
        default_procedure_stable_id: level.defaultProcedureStableId || category.defaultProcedureStableId,
        letter_abbreviation: category.letterAbbreviation,
        shape_marker: category.shapeMarker,
        is_pulp_involvement: !!level.isPulpInvolvement,
        is_custom: !!category.isCustom,
        is_palette_severity: true,
      }));
    }
  }
  return out;
}

export function buildPaletteCodeGroups(categories = []) {
  const entries = [];
  const categoryGroups = [];
  const conditionCodes = [];
  for (const category of categories) {
    const codes = [category.code, ...(category.severityLevels || []).map((level) => level.code)].filter(Boolean);
    categoryGroups.push({ categoryCode: category.code, codes });
    if (SINGLE_CONDITION_PEN_CATEGORIES.has(category.code)) conditionCodes.push(...codes);
  }
  for (const { categoryCode, codes } of categoryGroups) {
    const group = SINGLE_CONDITION_PEN_CATEGORIES.has(categoryCode) && conditionCodes.length > 0
      ? conditionCodes
      : codes;
    for (const code of codes) entries.push([code, group]);
  }
  return Object.fromEntries(entries);
}

function tutorialIdForPen(code) {
  return `charting-pen-${String(code || "").replace(/_/g, "-")}`;
}

export default function FindingsPalette({
  findings = [],
  paletteCategories = [],
  palettePreferences = null,
  active = null,
  disabled = false,
  onPickFinding,
  onPickEraser,
  onClear,
  onOpenAddCustom,
  onSavePreferences,
  canManageFindings = false,
  translateLoadedOnTabSwitch = true,
}) {
  const armedCode = active?.kind === "finding" ? active.code : null;
  const eraserArmed = active?.kind === "erase";
  const [activeTab, setActiveTab] = useState(palettePreferences?.activeTab || "core");
  const [expanded, setExpanded] = useState(!!palettePreferences?.morePaletteExpanded);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!palettePreferences) return;
    setActiveTab(palettePreferences.activeTab || "core");
    setExpanded(!!palettePreferences.morePaletteExpanded);
  }, [palettePreferences]);

  const queryText = query.trim().toLowerCase();
  const categories = useMemo(() => normalizeCategories(paletteCategories), [paletteCategories]);
  const fallbackFindings = useMemo(() => {
    const represented = new Set(flattenPaletteFindings(categories).map((f) => f.code));
    return findings.filter((f) => !represented.has(f.code) && f.code !== "note");
  }, [findings, categories]);
  const activePen = findPenByCode(armedCode, categories, fallbackFindings);
  const visible = useMemo(() => filterPalette(categories, activeTab, expanded, queryText), [categories, activeTab, expanded, queryText]);
  const visibleFallback = queryText
    ? fallbackFindings.filter((f) => String(f.display_name || f.name || f.code).toLowerCase().includes(queryText))
    : fallbackFindings.filter((f) => f.is_custom);

  function savePrefs(next) {
    onSavePreferences?.({
      activeTab,
      morePaletteExpanded: expanded,
      legendExpanded: palettePreferences?.legendExpanded ?? true,
      legendLabelMode: palettePreferences?.legendLabelMode || "clinical",
      showFindingLetters: palettePreferences?.showFindingLetters ?? true,
      ...next,
    });
  }

  function switchTab(tab) {
    if (tab === activeTab) return;
    const translatedCode = translateLoadedOnTabSwitch ? translateLoadedCodeForTab(armedCode, categories, tab) : null;
    setActiveTab(tab);
    savePrefs({ activeTab: tab });
    if (translatedCode && translatedCode !== armedCode) onPickFinding?.(translatedCode, { replace: true });
  }

  function toggleExpanded() {
    setExpanded((value) => {
      const next = !value;
      savePrefs({ morePaletteExpanded: next });
      return next;
    });
  }

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-slate-100 p-3 space-y-3 w-full"
      data-testid="findings-palette"
      data-tutorial-id="charting-findings-palette"
    >
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1" data-testid="palette-tabs">
        {["core", "detailed"].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => switchTab(tab)}
            disabled={disabled}
            data-testid={`palette-tab-${tab}`}
            data-tutorial-id={`palette-tab-${tab}`}
            className={`rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${
              activeTab === tab ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {tab === "core" ? "Core" : "Detailed"}
          </button>
        ))}
      </div>

      <LoadedPenIndicator activePen={activePen} eraserArmed={eraserArmed} disabled={disabled} onClear={onClear} />

      <label className="relative block">
        <span className="sr-only">Search findings</span>
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search pens..."
          data-testid="findings-search"
          className="w-full h-8 rounded-md border border-slate-200 bg-white pl-8 pr-2 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-500"
        />
      </label>

      {activeTab === "core" ? (
        <CorePalette
          categories={visible.core}
          armedCode={armedCode}
          disabled={disabled}
          onPickFinding={onPickFinding}
        />
      ) : (
        <DetailedPalette
          categories={visible.detailed}
          armedCode={armedCode}
          disabled={disabled}
          onPickFinding={onPickFinding}
        />
      )}

      {visible.single.length > 0 ? (
        <SinglePensSection
          categories={visible.single}
          armedCode={armedCode}
          disabled={disabled}
          onPickFinding={onPickFinding}
        />
      ) : null}

      {!queryText ? (
        <button
          type="button"
          onClick={toggleExpanded}
          data-testid="findings-more-toggle"
          className="w-full flex items-center justify-between px-1.5 py-1.5 text-[11px] font-medium text-slate-500 hover:text-slate-800"
        >
          <span className="inline-flex items-center gap-1"><Plus className="w-3 h-3" /> More findings</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      ) : null}

      {visibleFallback.length > 0 ? (
        <FallbackCatalog
          findings={visibleFallback}
          armedCode={armedCode}
          disabled={disabled}
          onPickFinding={onPickFinding}
        />
      ) : null}

      {visible.custom.length > 0 ? (
        <CustomPaletteSection
          categories={visible.custom}
          armedCode={armedCode}
          disabled={disabled}
          onPickFinding={onPickFinding}
        />
      ) : null}

      <div className="pt-2 border-t border-slate-100 flex items-center gap-2">
        <button
          type="button"
          onClick={onPickEraser}
          disabled={disabled}
          data-testid="findings-eraser"
          className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
            eraserArmed
              ? "border-rose-600 bg-rose-50 text-rose-700"
              : "border-slate-200 text-slate-700 bg-white hover:border-rose-400 hover:text-rose-600"
          }`}
        >
          <Eraser className="w-3.5 h-3.5" /> Eraser
        </button>
        {canManageFindings ? (
          <button
            type="button"
            onClick={onOpenAddCustom}
            disabled={disabled}
            data-testid="findings-add-custom"
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border border-dashed border-slate-300 text-sm font-medium text-slate-500 bg-white hover:border-teal-400 hover:text-teal-600 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Custom
          </button>
        ) : null}
      </div>
    </div>
  );
}

function LoadedPenIndicator({ activePen, eraserArmed, disabled, onClear }) {
  const label = activePen
    ? `Selected pen: ${activePen.loadedLabel}`
    : eraserArmed
      ? "Selected tool: Eraser"
      : "Tap a pen to load";
  return (
    <div
      className="rounded-md px-2.5 py-2 flex items-start justify-between gap-2 border"
      style={activePen ? loadedPenStyle(activePen) : { backgroundColor: "#f8fafc", borderColor: "#e2e8f0" }}
      data-testid="loaded-pen-indicator"
      data-tutorial-id="charting-selected-pen"
    >
      <div className="flex items-start gap-1.5 min-w-0">
        <Pencil className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: activePen ? "#0f766e" : "#64748b" }} />
        {activePen ? (
          <span
            className="w-2.5 h-2.5 rounded-full ring-1 ring-black/10 shrink-0 mt-1"
            style={{ backgroundColor: activePen.fill }}
            aria-hidden="true"
          />
        ) : null}
        <span className="text-xs font-semibold leading-snug break-words" style={{ color: activePen ? "#0f172a" : "#64748b" }}>
          {label}
        </span>
      </div>
      {(activePen || eraserArmed) ? (
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          data-testid="findings-clear"
          aria-label="Clear selected pen"
          className="p-0.5 rounded hover:bg-slate-100 shrink-0"
        >
          <X className="w-3.5 h-3.5 text-slate-500" />
        </button>
      ) : null}
    </div>
  );
}

function CorePalette({ categories, armedCode, disabled, onPickFinding }) {
  return (
    <div className="grid grid-cols-2 gap-1.5" data-testid="findings-list">
      {categories.map((category) => {
        const isArmed = armedCode === category.code;
        return (
          <button
            key={category.code}
            type="button"
            onClick={() => onPickFinding?.(category.code)}
            disabled={disabled}
            aria-label={category.chartLabel || category.label}
            aria-pressed={isArmed}
            data-testid={`finding-${category.code}`}
            data-tutorial-id={tutorialIdForPen(category.code)}
            title={category.clinicalTooltip || (category.patientLabel ? `${category.chartLabel || category.label} (${category.patientLabel})` : category.chartLabel || category.label)}
            className="min-h-[38px] rounded-lg px-2 py-2 text-[11px] font-semibold leading-tight transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400/40"
            style={categoryButtonStyle(category, isArmed)}
          >
            <span className="block truncate">{category.shortLabel || category.chartLabel || category.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function DetailedPalette({ categories, armedCode, disabled, onPickFinding }) {
  return (
    <div className="space-y-1.5" data-testid="findings-list">
      {categories.map((category) => {
        const activeLevel = (category.severityLevels || []).find((level) => level.code === armedCode);
        const activeBase = armedCode === category.code;
        const activeColor = activeLevel?.colorHex || (activeBase ? category.colorHex : null);
        return (
          <div
            key={category.code}
            className="flex items-center rounded-md py-1.5 pr-1.5 transition-colors"
            style={{
              backgroundColor: activeColor ? softColor(activeColor) : "transparent",
              borderLeft: activeColor ? `3px solid ${activeColor}` : "3px solid transparent",
            }}
            data-testid={`pen-category-${category.code}`}
          >
            <div className="w-[56px] shrink-0 pl-1.5 pr-1 text-[10px] font-semibold text-slate-800 uppercase truncate">
              {category.shortLabel || category.chartLabel || category.label}
            </div>
            <div className="flex flex-1 gap-1 min-w-0">
              {(category.severityLevels || []).map((level) => {
                const isArmed = armedCode === level.code;
                return (
                  <button
                    key={level.code}
                    type="button"
                    onClick={() => onPickFinding?.(level.code)}
                    disabled={disabled}
                    aria-label={`${category.chartLabel || category.label}, ${level.chartLabel || level.label} severity`}
                    aria-pressed={isArmed}
                    data-testid={`finding-${level.code}`}
                    data-tutorial-id={tutorialIdForPen(level.code)}
                    className="min-w-0 flex-1 rounded-md px-1 py-1 text-[10px] font-medium leading-none transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400/40"
                    style={severityButtonStyle(level, isArmed)}
                  >
                    <span className="block truncate">{level.chartLabel || level.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SinglePensSection({ categories, armedCode, disabled, onPickFinding }) {
  return (
    <div className="pt-2 border-t border-slate-200">
      <p className="text-[10px] font-semibold tracking-[0.03em] uppercase text-slate-400 px-1.5 mb-1.5">
        Whole Tooth
      </p>
      <div className="grid grid-cols-2 gap-1" data-testid="single-pens">
        {categories.map((category) => {
          const isArmed = armedCode === category.code;
          return (
            <button
              key={category.code}
              type="button"
              onClick={() => onPickFinding?.(category.code)}
              disabled={disabled}
              aria-label={category.chartLabel || category.label}
              aria-pressed={isArmed}
              data-testid={`finding-${category.code}`}
              data-tutorial-id={tutorialIdForPen(category.code)}
              className="px-2 py-1.5 rounded-md border text-[11px] font-medium transition-colors"
              style={singlePenButtonStyle(category, isArmed)}
            >
              <span className="block truncate">{category.shortLabel || category.chartLabel || category.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FallbackCatalog({ findings, armedCode, disabled, onPickFinding }) {
  return (
    <div className="pt-2 border-t border-slate-100 space-y-1">
      <p className="text-[10px] font-semibold tracking-[0.03em] uppercase text-slate-400 px-1.5">
        {findings.some((f) => f.is_custom) ? SECTION_LABELS.custom : SECTION_LABELS.legacy}
      </p>
      {findings.map((f) => {
        const isArmed = armedCode === f.code;
        return (
          <button
            key={f.id ?? f.code}
            type="button"
            onClick={() => onPickFinding?.(f.code)}
            disabled={disabled}
            data-testid={`finding-${f.code}`}
            data-tutorial-id={tutorialIdForPen(f.code)}
            title={f.is_custom ? `${f.display_name} (custom)` : f.display_name}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs font-medium transition-colors ${
              isArmed
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 text-slate-700 bg-white hover:border-slate-400"
            }`}
          >
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/10"
              style={{ backgroundColor: f.color_hex }}
            />
            <span className="truncate">{f.display_name}</span>
          </button>
        );
      })}
    </div>
  );
}

function CustomPaletteSection({ categories, armedCode, disabled, onPickFinding }) {
  return (
    <div className="pt-2 border-t border-slate-100 space-y-1">
      <p className="text-[10px] font-semibold tracking-[0.03em] uppercase text-slate-400 px-1.5">
        {SECTION_LABELS.custom}
      </p>
      {categories.map((category) => {
        const isArmed = armedCode === category.code;
        const label = category.chartLabel || category.label || category.code;
        const color = category.colorHex || "#64748b";
        return (
          <button
            key={category.id ?? category.code}
            type="button"
            onClick={() => onPickFinding?.(category.code)}
            disabled={disabled}
            data-testid={`finding-${category.code}`}
            data-tutorial-id={tutorialIdForPen(category.code)}
            title={`${label} (custom)`}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs font-medium transition-colors ${
              isArmed
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 text-slate-700 bg-white hover:border-slate-400"
            }`}
          >
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/10"
              style={{ backgroundColor: color }}
            />
            <span className="truncate">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

function normalizeCategories(categories = []) {
  return categories
    .map((rawCategory) => {
      const category = applyFindingPaletteColor(rawCategory);
      return normalizeChartFinding({
        ...category,
        label: category.label || category.display_name || category.code,
        chartLabel: category.chartLabel || category.chart_label || category.label || category.display_name || category.code,
        readbackLabel: category.readbackLabel || category.readback_label || category.patientLabel || category.patient_label || category.label || category.display_name || category.code,
        displayLabel: category.displayLabel || category.display_label || category.label || category.display_name || category.code,
        shortLabel: shortLabel(category.chartLabel || category.chart_label || category.label || category.display_name || category.code),
        patientLabel: category.patientLabel || category.patient_label,
        clinicalTooltip: category.clinicalTooltip || category.clinical_tooltip,
        colorHex: category.colorHex || category.color_hex || "#64748b",
        textColorHex: category.textColorHex || category.text_color_hex,
        requiresSurfaces: category.requiresSurfaces ?? category.requires_surfaces ?? true,
        displayOrder: category.displayOrder ?? category.display_order ?? 100,
        paletteSection: category.paletteSection || category.palette_section || "legacy",
        hasSeverity: category.hasSeverity ?? category.has_severity ?? false,
        defaultSeverityCode: category.defaultSeverityCode || category.default_severity_code,
        letterAbbreviation: category.letterAbbreviation || category.letter_abbreviation,
        shapeMarker: category.shapeMarker || category.shape_marker,
        singlePenStyle: category.singlePenStyle || category.single_pen_style || getSinglePenStyle(category.code),
        isCustom: category.isCustom ?? category.is_custom ?? false,
        severityLevels: (category.severityLevels || category.severity_levels || []).map((level) => ({
          ...level,
          label: level.chartLabel || level.chart_label || level.label || level.code,
          chartLabel: level.chartLabel || level.chart_label || level.label || level.code,
          readbackLabel: level.readbackLabel || level.readback_label || level.patientLabel || level.patient_label || level.label || level.code,
          patientLabel: level.patientLabel || level.patient_label || level.label || level.code,
          colorHex: level.colorHex || level.color_hex || category.colorHex || category.color_hex || "#64748b",
          textColorHex: level.textColorHex || level.text_color_hex,
          displayOrder: level.displayOrder ?? level.display_order ?? category.displayOrder ?? category.display_order ?? 100,
          shadeStop: level.shadeStop ?? level.shade_stop,
          isPulpInvolvement: level.isPulpInvolvement ?? level.is_pulp_involvement ?? false,
        })).sort((a, b) => Number(a.rank ?? 0) - Number(b.rank ?? 0)),
      });
    })
    .sort((a, b) => Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0));
}

function filterPalette(categories, activeTab, expanded, queryText) {
  const matches = (category) => {
    if (!queryText) return true;
    const haystack = [
      category.code,
      category.label,
      category.chartLabel,
      category.readbackLabel,
      category.clinicalTooltip,
      category.patientLabel,
      category.paletteSection,
      ...(category.severityLevels || []).flatMap((level) => [level.code, level.label, level.chartLabel, level.readbackLabel, level.patientLabel, level.rank]),
    ].join(" ").toLowerCase();
    return haystack.includes(queryText);
  };

  const matched = categories.filter(matches);
  const coreSections = expanded || queryText ? ["core_top10", "core_more", "expanded_more"] : ["core_top10"];
  const single = matched.filter((category) => isSinglePenCategory(category));
  const core = matched.filter((category) => coreSections.includes(category.paletteSection) && !isSinglePenCategory(category));
  const detailed = matched.filter((category) => (
    category.hasSeverity
    && !isSinglePenCategory(category)
    && (expanded || queryText || category.paletteSection === "core_top10" || category.paletteSection === "core_more")
  ));
  const custom = matched.filter((category) => category.paletteSection === "custom");
  return { core, detailed, single, custom };
}

function isSinglePenCategory(category) {
  return (
    SINGLE_CONDITION_PEN_CATEGORIES.has(category.code)
    || isSinglePenCode(category.code)
    || (!category.hasSeverity && category.paletteSection === "single_pen")
  );
}

function translateLoadedCodeForTab(code, categories, nextTab) {
  if (!code) return null;
  for (const category of categories) {
    if (nextTab === "detailed" && code === category.code && category.hasSeverity) {
      return category.defaultSeverityCode || category.severityLevels?.[1]?.code || category.severityLevels?.[0]?.code || code;
    }
    if (nextTab === "core" && (category.severityLevels || []).some((level) => level.code === code)) {
      return category.code;
    }
  }
  return code;
}

function findPenByCode(code, categories, fallbackFindings = []) {
  if (!code) return null;
  for (const category of categories) {
    if (category.code === code) {
      return {
        loadedLabel: category.chartLabel || category.label,
        fill: category.colorHex,
        border: category.colorHex,
        textColor: category.textColorHex || readableTextColor(category.colorHex),
      };
    }
    const level = (category.severityLevels || []).find((item) => item.code === code);
    if (level) {
      return {
        loadedLabel: `${category.chartLabel || category.label} · ${level.chartLabel || level.label}`,
        fill: level.colorHex,
        border: level.isPulpInvolvement ? PULP_OUTLINE_COLOR : level.colorHex,
        textColor: level.textColorHex || readableTextColor(level.colorHex),
        isPulpInvolvement: !!level.isPulpInvolvement,
      };
    }
  }
  const fallback = fallbackFindings.find((f) => f.code === code);
  return fallback ? {
    loadedLabel: fallback.display_name || fallback.name || fallback.code,
    fill: fallback.color_hex || NEUTRAL_ACTIVE.fill,
    border: fallback.color_hex || NEUTRAL_ACTIVE.border,
    textColor: readableTextColor(fallback.color_hex || NEUTRAL_ACTIVE.fill),
  } : null;
}

function categoryButtonStyle(category, isArmed) {
  const color = category.colorHex || "#64748b";
  return {
    backgroundColor: isArmed ? color : softColor(color),
    border: isArmed ? `1.5px solid ${darkenColor(color)}` : `0.5px solid ${color}`,
    color: isArmed ? (category.textColorHex || readableTextColor(color)) : darkenColor(color),
  };
}

function severityButtonStyle(level, isArmed) {
  const color = level.colorHex || "#64748b";
  return {
    backgroundColor: color,
    border: level.isPulpInvolvement
      ? `1.5px solid ${PULP_OUTLINE_COLOR}`
      : isArmed ? `1.5px solid ${darkenColor(color)}` : `0.5px solid ${darkenColor(color)}`,
    color: level.textColorHex || readableTextColor(color),
  };
}

function singlePenButtonStyle(category, isArmed) {
  const singlePen = category.singlePenStyle || getSinglePenStyle(category.code);
  const color = singlePen?.fillColor || category.colorHex || NEUTRAL_ACTIVE.border;
  const borderColor = singlePen?.borderColor || color;
  const textColor = singlePen?.textColor || category.textColorHex || readableTextColor(color);
  return {
    backgroundColor: isArmed ? color : softColor(color),
    border: isArmed ? `1.5px solid ${borderColor}` : `0.5px solid ${borderColor}`,
    color: isArmed ? textColor : darkenColor(borderColor),
  };
}

function loadedPenStyle(activePen) {
  return {
    backgroundColor: "#f8fafc",
    borderColor: activePen.isPulpInvolvement ? PULP_OUTLINE_COLOR : "#99f6e4",
    borderWidth: activePen.isPulpInvolvement ? "1.5px" : undefined,
    boxShadow: `inset 3px 0 0 ${activePen.fill}`,
  };
}

function softColor(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return "#f8fafc";
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`;
}

function darkenColor(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return "#334155";
  const factor = 0.65;
  return `rgb(${Math.round(rgb.r * factor)}, ${Math.round(rgb.g * factor)}, ${Math.round(rgb.b * factor)})`;
}

function parseHex(hex) {
  const clean = String(hex || "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

function shortLabel(label) {
  const value = String(label || "");
  if (value === "Defective Restoration") return "Defect";
  if (value === "Perio Pocket") return "Pocket";
  if (value === "Bleeding on Probing") return "Bleeding";
  if (value.length > 13) return value.split(" ")[0];
  return value;
}
